import { roleAtLeast, TENANT_ROLES, type CurrentUser, type Site, type TenantRole } from "@fts/core";
import type { Member } from "../data/provider";
import { descendantIds } from "./fleet";

/*
 * Client-side mirrors of the database rules, used only to decide which buttons to show.
 * The database (RLS + SECURITY DEFINER functions) remains the authority; a hidden button
 * is a convenience, not a security boundary.
 *
 * Every site a user can see is a site they can access (site scopes include descendants and
 * ancestors are not visible), so "can manage this site" reduces to the effective role.
 */

/** Add/edit vehicles and child sites, claim/pair/unpair devices. */
export const canManageFleet = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "fleet_manager");

/** Root sites and site deletion. */
export const isTenantAdmin = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "admin");

/** memberships_update / memberships_delete policies (ADR 0005: admin seats are platform-admin only). */
export function canManageMember(user: CurrentUser, role: TenantRole | null, member: Member): boolean {
  if (user.platformAdmin) return true;
  return isTenantAdmin(role) && member.userId !== user.id && member.role !== "admin";
}

/** Roles the user may grant (to a member or in an invitation). */
export function assignableRoles(user: CurrentUser): TenantRole[] {
  return user.platformAdmin ? [...TENANT_ROLES] : TENANT_ROLES.filter((r) => r !== "admin");
}

/** Manager KPI panel (ADR 0018): fleet managers and above (kpi_panel refuses everyone else). */
export const canSeeKpiPanel = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "fleet_manager");

/** "Live watch" (ADR 0014): safety officer and above; viewers only watch the map. */
export const canLiveWatch = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "safety");

/**
 * can_manage_site(site, 'safety') for one vehicle's site (request_live_watch): platform admin, or a
 * safety+ member whose site scope (with sub-sites) includes the site; an empty scope = every site.
 */
export function canLiveWatchSite(user: CurrentUser | null, tenantId: string, siteId: string, sites: Site[]): boolean {
  if (!user) return false;
  if (user.platformAdmin) return true;
  const m = user.memberships.find((x) => x.tenantId === tenantId);
  if (!m || !canLiveWatch(m.role)) return false;
  if (m.siteIds.length === 0) return true;
  return m.siteIds.some((root) => descendantIds(sites, root).has(siteId));
}
